import React, { useContext, useEffect, useState } from "react";
import { useApi} from "../../api";
import { Language } from "../../app_client";
import { useNavigate } from "react-router-dom";
import {
  YMButton,
  YMCard,
  YMInput,
  YMSelect,
  YMTable,
} from "../../ym-components";
import { Button, Spinner } from "flowbite-react";
import { SiDatabricks } from "react-icons/si";
import { HiTrash, HiPlus, HiPencil } from "react-icons/hi";
import { usePatternMatchingModelApi } from "./Api";
import ModelFilter from "./ModelFilter";
import { TagManager, WorkspaceTag } from "../../app_client/components";
import { WorkspaceTagType, WorkspaceModel, useWorkspaceApi } from "../workspace/Api";
import { AuthUserContext } from "../../AuthUserContext";

export default function PatternMatchingModelList(props: any) {
  const modelApi = usePatternMatchingModelApi();
  const workspaceApi = useWorkspaceApi();

  const { api, isAuthenticated } = useApi();
  const { workspaceId } = useContext(AuthUserContext) as {
    workspaceId: string;
  };
  const [models, setModels] = useState<WorkspaceModel[] | "loading">("loading");
  const navigate = useNavigate();

  const [lang, setLang] = useState<Language>(Language.FR);
  const [creatingModel, setCreatingModel] = useState(false);
  const [newModelName, setNewModelName] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [selectedModelForTags, setSelectedModelForTags] = useState<WorkspaceModel | null>(null);
  const [allTags, setAllTags] = useState<WorkspaceTagType[]>([]);

  // Fetch models on component mount
  useEffect(() => {
    if (modelApi) {
      modelApi.getModels(filterName || undefined, filterTagIds.length > 0 ? filterTagIds : undefined).then(setModels);
    }
  }, [modelApi, filterName, filterTagIds]);

  // Fetch workspace tags
  useEffect(() => {
    if (workspaceApi && workspaceId) {
      workspaceApi.getWorkspaceTags(workspaceId).then(setAllTags);
    }
  }, [workspaceApi, workspaceId]);

  const handleCreateModel = async () => {
    if (newModelName.length < 3) {
      alert("Le nom du modèle doit contenir au moins 3 caractères");
      return;
    }
    if (api && isAuthenticated) {
      setCreatingModel(true);
      modelApi
        .createModel({
          name: newModelName,
          patterns: [],
          language: lang,
        })
        .then(async ({ model_id }) => {
          // timeout to let the time to back to create the model in db
          setTimeout(() => {
            setCreatingModel(false);
            navigate(
              `/pattern_matching_models/edit?id=${model_id}&name=${newModelName}`
            );
          }, 1500);
        });
    }
  };

  const handleDeleteModel = async (model_id: string) => {
    if (api && isAuthenticated) {
      setModels(
        (models == "loading" ? [] : models).filter(
          (m) => m.model_id !== model_id
        )
      );
      api.delete_pattern_matching_model(model_id);
    }
  };

  const handleOnClickModel = (modelId: string, modelName: string) => {
    navigate(`/pattern_matching_models/edit?id=${modelId}&name=${modelName}`);
  };

  const handleOpenTagManager = (model: WorkspaceModel) => {
    setSelectedModelForTags(model);
    setShowTagManager(true);
  };

  const handleCloseTagManager = () => {
    setShowTagManager(false);
    setSelectedModelForTags(null);
  };

  const handleCreateTag = async (name: string, color: string): Promise<string> => {
    if (!workspaceApi || !workspaceId) {
      return Promise.reject("Workspace API not available");
    }

    const newTag = await workspaceApi.createWorkspaceTag(workspaceId, name, color);
    setAllTags([...allTags, newTag]);
    return newTag.id || newTag.name;
  };

  const handleSaveTags = async (tagNames: string[]): Promise<void> => {
    if (!selectedModelForTags) {
      return Promise.reject("No model selected");
    }

    if (!workspaceApi || !workspaceId) {
      return Promise.reject("Workspace API not available");
    }

    // Get tag IDs from tag names
    const tagIds = tagNames
      .map((name) => {
        const tag = allTags.find((t) => t.name === name);
        return tag?.id || tag?.name;
      })
      .filter((id): id is string => id !== undefined);

    // Call API to assign tags to model
    await workspaceApi.assignTagsToModel(workspaceId, selectedModelForTags.model_id, tagIds);

    // Update the model with the new tags in local state
    const updatedTags = tagNames.map((name) => {
      const tag = allTags.find((t) => t.name === name);
      return tag || { name, color: "gray" };
    });

    // Update the models state
    if (models !== "loading") {
      const updatedModels = models.map((m) =>
        m.model_id === selectedModelForTags.model_id
          ? { ...m, tags: updatedTags }
          : m
      );
      setModels(updatedModels);
    }

    return Promise.resolve();
  };

  return (
    <div>
      <YMCard>
        <div className="flex flex-col w-100">
          <div className="format">
            <h2 className="text-indigo-900">
              <SiDatabricks className="inline" /> Modèles à champs lexicaux
            </h2>
            <p className="-mt-5 text-sm font-normal text-indigo-900 dark:text-gray-400">
              Regroupez dans des champs lexicaux des mots et expressions
              similaires. Exemple : les mots "chat", "chien", "oiseau" peuvent
              se regrouper dans le champ lexical "animal".
            </p>
          </div>
          <div className="format mt-3 pt-3 border-t w-100 border-gray-200">
            <h4 className={"text-indigo-800"}> Créer un nouveau modèle </h4>
            <form className="flex flex-col gap-4">
              <div className={"flex flex gap-4"}>
                <div className={"w-1/2"}>
                  <YMInput
                    value={newModelName}
                    label={"Nom du modèle"}
                    onChange={(e) => setNewModelName(e)}
                  />
                </div>

                <YMSelect
                  label={"Langue"}
                  value={lang}
                  options={[
                    { id: Language.EN, name: "Anglais" },
                    { id: Language.FR, name: "Français" },
                  ]}
                  onChange={(v) => setLang(v as Language)}
                />
              </div>
              <div className={"w-20"}>
                <YMButton
                  text={"Créer"}
                  onClick={handleCreateModel}
                  icon={
                    creatingModel ? (
                      <div className={"mr-3"}>
                        <Spinner size="sm" light={true} />
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  disabled={creatingModel ? true : undefined}
                />
              </div>
            </form>
          </div>
        </div>
      </YMCard>
      <div className="mt-5">
        <ModelFilter
          onSearch={setFilterName}
          onTagsChange={setFilterTagIds}
          workspaceId={workspaceId}
          availableTags={allTags}
        />
      </div>
      {models === "loading" ? (
        <div className="w-100 mt-10 text-center">
          <Spinner color="gray" size="xl" aria-label="Default status example" />
          <div className="mt-4 text-gray-400">fetching models</div>
        </div>
      ) : models.length === 0 ? (
        <div className="w-100 mt-5 text-center">
          <p className="text-gray-500 mb-4">
            No pattern matching models available.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-lg mt-5">
          <YMTable
            header={
              <tr>
                <th scope="col" className="px-6 py-3">
                  Models
                </th>
                <th scope="col" className="px-6 py-3">
                  Tags
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only"> x </span>
                </th>
              </tr>
            }
            rows={models.map((m) => (
              <tr
                key={m.model_id}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100"
              >
                <td
                  scope="row"
                  className="px-6 py-4 text-indigo-900 cursor-pointer capitalize font-bold max-w-[48px] hover:text-indigo-900 dark:text-white"
                  onClick={() => handleOnClickModel(m.model_id, m.name)}
                >
                  {m.name}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.tags?.map((tag) => (
                      <WorkspaceTag key={tag.name} tag={tag} />
                    ))}
                    <Button
                      size="xs"
                      color="indigo"
                      onClick={() => handleOpenTagManager(m)}
                      title="Manage tags"
                    >
                      <HiPencil className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
                <td className="px-6 py-4 flex gap-2">
                  <Button
                    size="xs"
                    color="gray"
                    onClick={() => handleDeleteModel(m.model_id)}
                  >
                    <HiTrash className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          />
        </div>
      )}
      {showTagManager && selectedModelForTags && (
        <TagManager
          show={showTagManager}
          onClose={handleCloseTagManager}
          existingTags={allTags}
          selectedTags={selectedModelForTags.tags || []}
          onCreateTag={handleCreateTag}
          onSaveTags={handleSaveTags}
        />
      )}
    </div>
  );
}
